/**
 * PR-27 — Draggable Floating Button system.
 * Lets users drag any FAB across the viewport, snaps to nearest edge,
 * persists per-id position in localStorage, and respects safe-area insets.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "@/lib/audit";

export type DraggableFabId =
  | "share_fab"
  | "help_fab"
  | "halftime_fab"
  | "focus_fab"
  | "participate_fab"
  | "reward_fab"
  | "retention_fab";

export interface FabPosition {
  x: number; // px from left
  y: number; // px from top
  edge: "left" | "right";
}

const KEY = "vs:pr27:fab_positions";
const MARGIN = 12;

function readAll(): Record<string, FabPosition> {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(KEY) ?? "{}"); } catch { return {}; }
}
function writeAll(m: Record<string, FabPosition>): void {
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(m));
}

export function getFabPosition(id: DraggableFabId): FabPosition | undefined {
  return readAll()[id];
}

export function setFabPosition(id: DraggableFabId, pos: FabPosition): void {
  const all = readAll();
  all[id] = pos;
  writeAll(all);
}

export function resetFabPositions(): void {
  if (typeof window !== "undefined") window.localStorage.removeItem(KEY);
  void trackEvent("floating_positions_reset", {});
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ss2026:fab-reset"));
  }
}

/** Auto-arrange stacks all FABs along the right edge with even vertical spacing. */
export function autoArrangeFabs(ids: DraggableFabId[]): void {
  if (typeof window === "undefined") return;
  const w = window.innerWidth;
  const h = window.innerHeight;
  const map: Record<string, FabPosition> = {};
  ids.forEach((id, i) => {
    map[id] = { x: w - 60 - MARGIN, y: h - 100 - i * 60, edge: "right" };
  });
  writeAll(map);
  void trackEvent("floating_positions_autoarranged", { count: ids.length });
  window.dispatchEvent(new CustomEvent("ss2026:fab-reset"));
}

/**
 * Hook that returns a ref, inline style, and pointer handlers to make any
 * fixed-positioned button draggable. Falls back to its CSS layout when no
 * user position is stored.
 */
export function useDraggableFab(id: DraggableFabId): {
  ref: React.RefObject<HTMLButtonElement | null>;
  style: React.CSSProperties;
  onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => void;
  isDragging: boolean;
} {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<FabPosition | undefined>(() => getFabPosition(id));
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ px: number; py: number; ox: number; oy: number; moved: boolean } | null>(null);

  useEffect(() => {
    const refresh = () => setPos(getFabPosition(id));
    if (typeof window === "undefined") return;
    window.addEventListener("ss2026:fab-reset", refresh);
    return () => window.removeEventListener("ss2026:fab-reset", refresh);
  }, [id]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    startRef.current = { px: e.clientX, py: e.clientY, ox: rect.left, oy: rect.top, moved: false };
    try { ref.current.setPointerCapture(e.pointerId); } catch { /* noop */ }

    const move = (ev: PointerEvent) => {
      if (!startRef.current) return;
      const dx = ev.clientX - startRef.current.px;
      const dy = ev.clientY - startRef.current.py;
      if (Math.abs(dx) + Math.abs(dy) > 4) {
        if (!startRef.current.moved) {
          startRef.current.moved = true;
          setDragging(true);
          void trackEvent("floating_button_drag_started", { id });
        }
        ev.preventDefault();
        const x = Math.max(MARGIN, Math.min(window.innerWidth - rect.width - MARGIN, startRef.current.ox + dx));
        const y = Math.max(MARGIN, Math.min(window.innerHeight - rect.height - MARGIN, startRef.current.oy + dy));
        setPos({ x, y, edge: x < window.innerWidth / 2 ? "left" : "right" });
      }
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (!startRef.current || !ref.current) return;
      const moved = startRef.current.moved;
      startRef.current = null;
      // Defer clearing dragging so the synthetic click is suppressed.
      window.setTimeout(() => setDragging(false), moved ? 220 : 0);
      if (!moved) return;
      // Snap to nearest horizontal edge
      const r = ref.current.getBoundingClientRect();
      const edge: "left" | "right" = ev.clientX < window.innerWidth / 2 ? "left" : "right";
      const snapped: FabPosition = {
        x: edge === "left" ? MARGIN : window.innerWidth - r.width - MARGIN,
        y: Math.max(MARGIN, Math.min(window.innerHeight - r.height - MARGIN, r.top)),
        edge,
      };
      setPos(snapped);
      setFabPosition(id, snapped);
      void trackEvent("floating_button_dragged", { id, x: snapped.x, y: snapped.y, edge });
      void trackEvent("floating_button_drag_completed", { id, edge });
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, [id]);

  const style: React.CSSProperties = pos
    ? { position: "fixed", left: pos.x, top: pos.y, right: "auto", bottom: "auto", touchAction: "none" }
    : { touchAction: "none" };

  return { ref, style, onPointerDown, isDragging: dragging };
}